/**
 * Editor da captura de um passo, igual na extensão e na app web: destaques e setas (vetores
 * no passo, a imagem não muda), desfocar zonas (fica na imagem: quem chama aplica-o),
 * recortar e mudar o ponto do clique. Quem chama decide como guarda.
 */
import {
  ANNOTATION_COLORS,
  annotationHex,
  MAX_ANNOTATIONS,
  rectFrom,
  type Annotation,
  type AnnotationColor,
  type Box,
  type Point,
  type Step,
} from '@nolens/domain';
import { useRef, useState, type ReactNode } from 'react';
import { AnnotationLayer } from './Annotations';
import { Button } from './Button';
import { SegmentedControl } from './Controls';
import { cx } from './cx';
import { Dialog } from './Dialog';
import { useLocale } from './locale';
import { uiText } from './ui-text';

export type ImageTool = 'box' | 'arrow' | 'blur' | 'crop' | 'point';

export interface ImageEdit {
  crop: Box | undefined;
  point: Point | undefined;
  annotations: Annotation[] | undefined;
  /** Zonas a desfocar na imagem (irreversível). */
  blurs: Box[];
}

export function ImageEditorDialog({
  step,
  src,
  onClose,
  onSave,
  saving = false,
  blurHint,
  tools = ['box', 'arrow', 'blur', 'crop', 'point'],
  extraTools,
  footerStart,
}: {
  step: Step;
  src: string | null | undefined;
  onClose: () => void;
  onSave: (edit: ImageEdit) => void;
  saving?: boolean;
  blurHint?: string;
  tools?: ImageTool[];
  /** Ao lado das ferramentas (ex.: «Substituir imagem»). */
  extraTools?: ReactNode;
  /** No rodapé, à esquerda (ex.: «Restaurar original»). */
  footerStart?: ReactNode;
}) {
  const t = uiText(useLocale());
  const toolLabel: Record<ImageTool, string> = {
    box: t.toolBox,
    arrow: t.toolArrow,
    blur: t.toolBlur,
    crop: t.toolCrop,
    point: t.toolPoint,
  };
  const [mode, setMode] = useState<ImageTool>(tools[0] ?? 'box');
  const [color, setColor] = useState<AnnotationColor>('red');
  const [crop, setCrop] = useState<Box | undefined>(step.crop);
  const [point, setPoint] = useState<Point | undefined>(step.point);
  const [annotations, setAnnotations] = useState<Annotation[]>(step.annotations ?? []);
  const [blurs, setBlurs] = useState<Box[]>([]);
  const [drag, setDrag] = useState<{ a: Point; b: Point } | null>(null);
  const [aspect, setAspect] = useState(step.viewport ? step.viewport.w / step.viewport.h : 16 / 10);
  const area = useRef<HTMLDivElement>(null);

  const at = (e: React.PointerEvent): Point => {
    const r = area.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  function down(e: React.PointerEvent) {
    if (mode === 'point') {
      setPoint(at(e));
      return;
    }
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const p = at(e);
    setDrag({ a: p, b: p });
  }
  function move(e: React.PointerEvent) {
    if (drag) setDrag({ a: drag.a, b: at(e) });
  }
  const add = (a: Annotation) => setAnnotations((l) => [...l, a].slice(0, MAX_ANNOTATIONS));

  function up() {
    if (!drag) return;
    const d = drag;
    setDrag(null);
    if (mode === 'arrow') {
      if (Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y) < 0.02) return;
      add({ kind: 'arrow', color, from: d.a, to: d.b });
      return;
    }
    const r = rectFrom(d.a, d.b);
    if (r.w < 0.01 || r.h < 0.01) return;
    if (mode === 'crop') setCrop(r);
    else if (mode === 'blur') setBlurs((b) => [...b, r]);
    else if (mode === 'box') add({ kind: 'box', color, ...r });
  }

  const shown = drag ? rectFrom(drag.a, drag.b) : null;
  const preview: Annotation | null =
    drag && mode === 'arrow'
      ? { kind: 'arrow', color, from: drag.a, to: drag.b }
      : shown && mode === 'box'
        ? { kind: 'box', color, ...shown }
        : null;
  const pct = (b: Box) => ({
    left: b.x * 100 + '%',
    top: b.y * 100 + '%',
    width: b.w * 100 + '%',
    height: b.h * 100 + '%',
  });
  const drawing = mode === 'box' || mode === 'arrow';

  const description = {
    box: t.hintBox,
    arrow: t.hintArrow,
    blur: blurHint ?? t.hintBlur,
    crop: t.hintCrop,
    point: t.hintPoint,
  }[mode];

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={t.editImage}
      description={description}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          {footerStart}
          <span className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button
            loading={saving}
            onClick={() =>
              onSave({
                crop,
                point,
                annotations: annotations.length ? annotations : undefined,
                blurs,
              })
            }
          >
            {t.save}
          </Button>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl<ImageTool>
          label={t.tool}
          value={mode}
          onChange={setMode}
          options={tools.map((tool) => ({ value: tool, label: toolLabel[tool] }))}
        />
        <span className="flex-1" />
        {extraTools}
      </div>
      <div className="mt-3 flex min-h-9 flex-wrap items-center gap-2">
        {drawing && (
          <div role="radiogroup" aria-label={t.color} className="flex items-center gap-1.5">
            {(Object.keys(ANNOTATION_COLORS) as AnnotationColor[]).map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                aria-label={t.colorName(c)}
                title={t.colorName(c)}
                onClick={() => setColor(c)}
                className={cx(
                  'size-6 rounded-full transition',
                  color === c
                    ? 'ring-2 ring-text-1 ring-offset-2 ring-offset-surface-1'
                    : 'opacity-80 hover:opacity-100',
                )}
                style={{ background: annotationHex({ color: c }) }}
              />
            ))}
          </div>
        )}
        <span className="flex-1" />
        {drawing && !!annotations.length && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setAnnotations((l) => l.slice(0, -1))}>
              {t.undo}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAnnotations([])}>
              {t.clearAll}
            </Button>
          </>
        )}
        {mode === 'blur' && !!blurs.length && (
          <Button variant="ghost" size="sm" onClick={() => setBlurs((b) => b.slice(0, -1))}>
            {t.undo}
          </Button>
        )}
        {mode === 'crop' && crop && (
          <Button variant="ghost" size="sm" onClick={() => setCrop(undefined)}>
            {t.noCrop}
          </Button>
        )}
        {mode === 'point' && point && (
          <Button variant="ghost" size="sm" onClick={() => setPoint(undefined)}>
            {t.noMarker}
          </Button>
        )}
      </div>
      <div
        ref={area}
        className="relative mt-3 touch-none select-none overflow-hidden rounded-[10px] bg-surface-2"
        style={{
          cursor: mode === 'point' ? 'pointer' : 'crosshair',
          aspectRatio: src ? undefined : String(aspect),
        }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        data-testid="image-area"
      >
        {src && (
          <img
            src={src}
            alt={t.stepScreenshot}
            className="block w-full"
            draggable={false}
            onLoad={(e) =>
              setAspect(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || aspect)
            }
          />
        )}
        <AnnotationLayer
          annotations={preview ? [...annotations, preview] : annotations}
          aspect={aspect}
        />
        {crop && (
          <div
            className="pointer-events-none absolute rounded-[4px] shadow-[0_0_0_9999px_rgba(13,14,16,.45)] outline-2 outline-white"
            style={pct(crop)}
          />
        )}
        {blurs.map((b, i) => (
          <div
            key={i}
            className="pointer-events-none absolute rounded-[3px] bg-[rgba(43,46,51,.55)] backdrop-blur-md"
            style={pct(b)}
          />
        ))}
        {shown && (mode === 'crop' || mode === 'blur') && (
          <div
            className={cx(
              'pointer-events-none absolute rounded-[3px] border-2 border-dashed',
              mode === 'crop' ? 'border-white' : 'border-attention',
            )}
            style={pct(shown)}
          />
        )}
        {point && (
          <div
            className="pointer-events-none absolute size-9 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-accent bg-accent/25"
            style={{ left: point.x * 100 + '%', top: point.y * 100 + '%' }}
          />
        )}
      </div>
    </Dialog>
  );
}
